import React, { useMemo, useState } from 'react';
import { Priority, PriorityLabels, Project, QuickTaskInput, User } from '../../types';
import { MemberSearchSelect } from './MemberSearchSelect';
import { validateDateRange, validateEstimatedHours } from '../../utils/format';
import './QuickTaskModal.css';

interface QuickTaskModalProps {
  isOpen: boolean;
  project: Project | null;
  members: User[];
  onSubmit: (task: QuickTaskInput) => Promise<{ success: boolean; error?: string }>;
  onClose: () => void;
}

/**
 * Alta rápida de tarea sin construir el árbol, al estilo JIRA.
 *
 * Todos los campos salvo el nombre son opcionales: dejarlos vacíos produce
 * exactamente el mismo resultado que el formulario reducido original.
 */
export const QuickTaskModal: React.FC<QuickTaskModalProps> = ({
  isOpen, project, members, onSubmit, onClose,
}) => {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<Priority>('MEDIUM');
  const [assignedTo, setAssignedTo] = useState<string[]>([]);
  const [estimatedHours, setEstimatedHours] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [elementId, setElementId] = useState('');
  const [showMore, setShowMore] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Destinos del árbol, aplanados a "Zona > Sección > Elemento".
  const destinations = useMemo(() => {
    if (!project) return [];
    return project.zones.flatMap(z =>
      z.sections.flatMap(s =>
        s.elements.map(e => ({
          id: e.id,
          label: `${z.name} > ${s.name} > ${e.name}`,
        }))
      )
    );
  }, [project]);

  if (!isOpen || !project) return null;

  const reset = () => {
    setCode(''); setName(''); setDescription(''); setPriority('MEDIUM');
    setAssignedTo([]); setEstimatedHours(''); setStartDate(''); setEndDate('');
    setElementId(''); setShowMore(false); setError('');
  };

  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async () => {
    if (!name.trim()) { setError('Task name is required.'); return; }

    if (estimatedHours.trim()) {
      const hoursError = validateEstimatedHours(estimatedHours);
      if (hoursError) { setError(hoursError); return; }
    }
    if (startDate && endDate) {
      const dateError = validateDateRange(startDate, endDate);
      if (dateError) { setError(dateError); return; }
    }

    setError('');
    setSaving(true);
    const result = await onSubmit({
      code: code.trim() || 'TASK',
      name: name.trim(),
      priority,
      description: description.trim() || undefined,
      assignedTo: assignedTo.length > 0 ? assignedTo : undefined,
      estimatedHours: estimatedHours.trim() ? Number(estimatedHours) : undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      elementId: elementId || undefined,
    });
    setSaving(false);

    if (!result.success) { setError(result.error || 'Could not create the task.'); return; }
    reset();
    onClose();
  };

  return (
    <div className="status-modal-backdrop" onClick={handleClose}>
      <div className="status-modal quick-task-modal" onClick={e => e.stopPropagation()}>
        <div className="status-modal-header">
          <h4>New Task</h4>
          <button className="status-modal-close" onClick={handleClose}>&times;</button>
        </div>

        <div className="status-modal-body">
          <p className="qt-hint">
            Creates the task directly, with no need to build the structure first.
            Leave <em>Location</em> untouched and it lands under “General” — you can move it later.
          </p>
          <div className="qt-row">
            <div className="status-modal-field qt-code">
              <label>Code</label>
              <input
                type="text"
                value={code}
                onChange={e => setCode(e.target.value)}
                placeholder="TASK"
              />
            </div>
            <div className="status-modal-field qt-name">
              <label>Task name <span className="required">*</span></label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="What needs to be done?"
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
              />
            </div>
          </div>

          {!showMore ? (
            <button type="button" className="qt-more-toggle" onClick={() => setShowMore(true)}>
              + Add details (assignee, dates, priority…)
            </button>
          ) : (
            <>
              <div className="status-modal-field">
                <label>Description</label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  rows={2}
                  placeholder="Optional details"
                />
              </div>

              <div className="qt-row">
                <div className="status-modal-field">
                  <label>Priority</label>
                  <select value={priority} onChange={e => setPriority(e.target.value as Priority)}>
                    {(Object.keys(PriorityLabels) as Priority[]).map(p => (
                      <option key={p} value={p}>{PriorityLabels[p]}</option>
                    ))}
                  </select>
                </div>
                <div className="status-modal-field">
                  <label>Estimated hours</label>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={estimatedHours}
                    onChange={e => setEstimatedHours(e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="qt-row">
                <div className="status-modal-field">
                  <label>Start date</label>
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                </div>
                <div className="status-modal-field">
                  <label>Due date</label>
                  <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                </div>
              </div>

              <div className="status-modal-field">
                <label>Assign to</label>
                <MemberSearchSelect
                  users={members}
                  selectedIds={assignedTo}
                  onToggle={userId => setAssignedTo(prev =>
                    prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
                  )}
                  multiSelect
                  placeholder="Select personnel…"
                />
              </div>

              <div className="status-modal-field">
                <label>Location</label>
                <select value={elementId} onChange={e => setElementId(e.target.value)}>
                  <option value="">General (default)</option>
                  {destinations.map(d => (
                    <option key={d.id} value={d.id}>{d.label}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {error && <div className="status-modal-error">{error}</div>}
        </div>

        <div className="status-modal-footer">
          <button className="status-modal-cancel" onClick={handleClose} disabled={saving}>Cancel</button>
          <button className="status-modal-confirm" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Creating…' : 'Create task'}
          </button>
        </div>
      </div>
    </div>
  );
};
